'use client';

// ShareEntityButton — the canonical "Share" trigger for any first-party entity
// (resource / circle / network / event / post). Icon-only by default — the
// share glyph is universally understood — with an opt-in visible label for
// header action rows where every sibling button carries one (the resource
// detail header). Click opens ShareEntityModal with the entity pre-attached.

import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Button, I } from '@leanin/ui';
import { ShareEntityModal, type ShareEntityRef } from './ShareEntityModal';

export type ShareEntityButtonProps = {
  entity: ShareEntityRef;
  /** Optional screen-reader label override. Defaults to "Share" — pass a
   *  contextual variant (e.g. "Share event") when the parent action cluster
   *  has other Share-like triggers nearby and disambiguation helps. */
  ariaLabel?: string;
  /** Visual weight. Detail-page header rows usually want `secondary`; card
   *  overflow slots usually want `ghost`. */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button height tier. */
  size?: 'sm' | 'md';
  /** Render the "Share" label next to the glyph (default: icon-only). */
  showLabel?: boolean;
};

export function ShareEntityButton({
  entity,
  ariaLabel,
  variant = 'secondary',
  size = 'md',
  showLabel = false,
}: ShareEntityButtonProps) {
  const t = useTranslations('Feed');
  const [open, setOpen] = useState(false);
  const label = ariaLabel ?? t('share.buttonAria');

  // Modal is mount-gated so we don't pay for the resolver query or the
  // composer's draft-load until the user actually opens it.
  return (
    <>
      <Button
        variant={variant}
        size={size}
        icon={I.share}
        aria-label={label}
        onClick={() => setOpen(true)}
      >
        {showLabel ? t('share.buttonLabel') : undefined}
      </Button>
      {open ? (
        <ShareEntityModal open={open} onOpenChange={setOpen} entity={entity} />
      ) : null}
    </>
  );
}
